import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type { TvShow } from "../../../services/apiTypes"
import ShowCard from '../../show/components/ShowCard'
import styles from '../../../App.module.css'
import useShows from '../hooks/useShows'
import { isRunningShow } from '../../show/utils/show'
import FilterButton from '../../../components/common/FilterButton'
import useGenreFilter from '../../../hooks/useGenreFilter'
import GenreSelect from '../../../components/common/GenreSelect'
import LibraryStatusBanner from '../../../components/common/LibraryStatusBanner'

type WatchingFilter = 'inProgress' | 'notStarted' | 'completed' | 'running' | 'unplanned' | 'all'

const watchingFilters: Array<{ key: WatchingFilter; label: string; tooltip: string }> = [
	{ key: 'inProgress', label: 'Continue', tooltip: 'Shows where you\'ve watched at least one episode and still have more to go.' },
	{ key: 'notStarted', label: 'Not started', tooltip: 'Shows you follow but haven\'t watched a single episode of yet.' },
	{ key: 'completed', label: 'Completed', tooltip: 'Shows where you\'ve watched all available episodes and there\'s no next episode queued.' },
	{ key: 'running', label: 'Running', tooltip: 'Currently airing shows — TMDb has a future episode date for these.' },
	{ key: 'unplanned', label: 'Unplanned', tooltip: 'Shows with no announced next air date (ended, cancelled, or hiatus).' },
	{ key: 'all', label: 'All', tooltip: 'Every show in your library regardless of status.' },
]

function isNotStarted(show: TvShow) {
	return show.episodesWatched === 0
}

function isInProgress(show: TvShow) {
	return show.episodesWatched > 0 && Boolean(show.nextUserEpisode)
}

function isCompleted(show: TvShow) {
	return show.episodesWatched > 0 && !show.nextUserEpisode
}

function matchesFilter(show: TvShow, filter: WatchingFilter) {
	switch(filter) {
		case 'inProgress':
			return isInProgress(show)
		case 'notStarted':
			return isNotStarted(show)
		case 'completed':
			return isCompleted(show)
		case 'running':
			return isRunningShow(show)
		case 'unplanned':
			return !isRunningShow(show)
		case 'all':
			return true
	}
}

function sortShows(shows: TvShow[], filter: WatchingFilter) {
	const byTitle = [...shows].sort((a, b) => a.title.localeCompare(b.title))

	switch(filter) {
		case 'running':
			return [...shows].sort((a, b) => {
				const aTime = a.nextAiringEpisode?.releaseDate ? new Date(a.nextAiringEpisode.releaseDate).getTime() : Number.MAX_SAFE_INTEGER
				const bTime = b.nextAiringEpisode?.releaseDate ? new Date(b.nextAiringEpisode.releaseDate).getTime() : Number.MAX_SAFE_INTEGER
				if(aTime !== bTime) return aTime - bTime
				return a.title.localeCompare(b.title)
			})
		case 'inProgress':
			return [...shows].sort((a, b) => {
				const aTime = a.nextUserEpisode?.releaseDate ? new Date(a.nextUserEpisode.releaseDate).getTime() : Number.MAX_SAFE_INTEGER
				const bTime = b.nextUserEpisode?.releaseDate ? new Date(b.nextUserEpisode.releaseDate).getTime() : Number.MAX_SAFE_INTEGER
				if(aTime !== bTime) return aTime - bTime
				return a.title.localeCompare(b.title)
			})
		case 'all':
			return byTitle
		default:
			return byTitle
	}
}

function getEmptyState(filter: WatchingFilter) {
	switch(filter) {
		case 'inProgress':
			return 'No shows in progress yet.'
		case 'notStarted':
			return 'No not started shows yet.'
		case 'completed':
			return 'No completed shows yet.'
		case 'running':
			return 'No running shows yet.'
		case 'unplanned':
			return 'No unplanned shows yet.'
		case 'all':
			return 'No followed shows yet. Use search to add your next series.'
	}
}

export default function WatchingPage() {
	const navigate = useNavigate()
	const [searchParams, setSearchParams] = useSearchParams()
	const { shows, loading, error, retry } = useShows('watching')
	const watchingFilter = (searchParams.get('filter') as WatchingFilter | null) ?? 'running'
	const [libraryQuery, setLibraryQuery] = useState(() => sessionStorage.getItem('watching_search') ?? '')
	const [genre, setGenre] = useState(() => sessionStorage.getItem('watching_genre') ?? '')
	const [searchOpen, setSearchOpen] = useState(Boolean(libraryQuery))
	const searchInputRef = useRef<HTMLInputElement>(null)
	const { genres, counts, loading: genresLoading, filter: filterByGenre } = useGenreFilter(shows)

	useEffect(() => {
		sessionStorage.setItem('watching_search', libraryQuery)
	}, [libraryQuery])

	useEffect(() => {
		sessionStorage.setItem('watching_genre', genre)
	}, [genre])

	useEffect(() => {
		if(searchOpen) searchInputRef.current?.focus()
	}, [searchOpen])

	const filteredShows = useMemo(() => {
		const base = shows.filter((show) => matchesFilter(show, watchingFilter))
		const sorted = sortShows(base, watchingFilter)
		const q = libraryQuery.toLowerCase().trim()
		return filterByGenre(q, genre).filter((s) => sorted.some((sortedShow) => sortedShow.id === s.id))
	}, [shows, watchingFilter, libraryQuery, filterByGenre, genre])

	useLayoutEffect(() => {
		if(loading || (genre && genresLoading)) {
			return
		}

		const savedScroll = sessionStorage.getItem('watching_scroll')
		if(savedScroll === null) {
			return
		}

		sessionStorage.removeItem('watching_scroll')
		const scrollTop = Number(savedScroll)
		if(Number.isFinite(scrollTop)) {
			window.scrollTo({ top: scrollTop, behavior: 'instant' as ScrollBehavior })
		}
	}, [loading, genresLoading, filteredShows, genre])

	function setWatchingFilter(filter: WatchingFilter) {
		const params = new URLSearchParams(searchParams)
		if(filter === 'running') {
			params.delete('filter')
		} else {
			params.set('filter', filter)
		}
		setSearchParams(params)
	}

	function handleShowClick(show: TvShow) {
		sessionStorage.setItem('watching_scroll', String(window.scrollY))
		navigate(`/show/${encodeURIComponent(show.id)}`)
	}

	if(loading) {
		return (
			<main className={styles.mainPanel}>
				<LibraryStatusBanner />
				<div className={styles.loadingBar} />
			</main>
		)
	}
	if(error) {
		return <main className={styles.mainPanel}><LibraryStatusBanner /><div className={styles.emptyState} role="alert"><p>Could not load shows: {error}</p><button className={styles.secondaryButton} type="button" onClick={() => void retry()}>Try again</button></div></main>
	}

	return (
		<main className={styles.mainPanel}>
			<LibraryStatusBanner />
			<section className={styles.tabContent}>
				<div className={`${styles.watchingToolbar} ${styles.myShowsToolbar}`}>
					<div className={styles.sortControls}>
						{watchingFilters.map((filter) => (
							<FilterButton
								key={filter.key}
								label={filter.label}
								active={watchingFilter === filter.key}
								tooltip={filter.tooltip}
								onClick={() => setWatchingFilter(filter.key)}
							/>
						))}
						{!searchOpen && (
							<>
								<button className={styles.librarySearchIconButton} onClick={() => setSearchOpen(true)} type="button" aria-label="Search my shows">⌕</button>
								<GenreSelect genres={genres} counts={counts} loading={genresLoading} value={genre} onChange={setGenre} />
							</>
						)}
					</div>
					{searchOpen && <div className={styles.librarySearchWrap}>
						<div className={styles.searchInputWrap}>
							<input
								ref={searchInputRef}
								type="text"
								value={libraryQuery}
								onChange={(e) => setLibraryQuery(e.target.value)}
								placeholder="Filter by show name..."
								className={styles.librarySearchInput}
							/>
							{libraryQuery && <button onClick={() => setLibraryQuery('')} className={styles.librarySearchClear} type="button" title="Clear filter">✕</button>}
						</div>
						<GenreSelect genres={genres} counts={counts} loading={genresLoading} value={genre} onChange={setGenre} />
						{libraryQuery && (
							<button
								onClick={() => setLibraryQuery('')}
								className={styles.librarySearchClear}
								type="button"
								title="Clear filter"
							>
								✕
							</button>
						)}
					</div>}
				</div>

				{genre && genresLoading && filteredShows.length === 0 ? (
					<div className={styles.loadingBar} />
				) : filteredShows.length === 0 ? (
					<div className={styles.emptyState}>
						{libraryQuery ? (
							<div>
								<p>No followed shows matching "{libraryQuery}" in this view.</p>
								<button
									className={styles.globalSearchButton}
									onClick={() => navigate(`/search?q=${encodeURIComponent(libraryQuery)}`)}
									type="button"
								>
									Search TMDb globally
								</button>
							</div>
						) : (
							getEmptyState(watchingFilter)
						)}
					</div>
				) : (
					<div className={styles.showGrid}>
						{filteredShows.map((show) => (
							<ShowCard
								key={show.id}
								show={show}
								onClick={handleShowClick}
								compact
							/>
						))}
					</div>
				)}
			</section>
		</main>
	)
}
